import { useEffect, useRef, useState } from "react"
import gsap from "gsap"
import styled from "styled-components"
import { imagePathsScreen, ImageCategory } from "./GalleryData"
import useStore from "@state/store"
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch"
import { useSyncStatesWithSocket } from "@utils/useSyncStatesWithSockets"
import Carousel from "./Carousel"

type Props = {
  isVisible: boolean
}

const Gallery = (props: Props) => {
  const galleryRef = useRef<HTMLDivElement>(null)
  const [images, setImages] = useState<string[]>([])
  const { selectedGalleryImage } = useStore((s) => ({
    selectedGalleryImage: s.selectedGalleryImage,
  }))
  const { selectedGalleryCategory, zoomRef } = useStore((s) => ({
    selectedGalleryCategory: s.selectedGalleryCategory,
    zoomRef: s.zoomRef,
  }))
  const transformWrapperRef = useRef(null)

  const [updatedCategory, setUpdatedCategory] = useState(
    selectedGalleryCategory,
  )

  const [imageSrc, setImageSrc] = useState(
    selectedGalleryImage !== null
      ? imagePathsScreen[selectedGalleryCategory].paths[selectedGalleryImage]
      : imagePathsScreen[selectedGalleryCategory].paths[0],
  )

  useEffect(() => {
    setImages(imagePathsScreen[selectedGalleryCategory].paths)
  }, [selectedGalleryCategory])

  useEffect(() => {
    if (selectedGalleryImage !== null) {
      setUpdatedCategory(selectedGalleryCategory)
      const src =
        imagePathsScreen[selectedGalleryCategory].paths[selectedGalleryImage]
      setImageSrc(src)
    }
  }, [selectedGalleryCategory, selectedGalleryImage])

  useEffect(() => {
    if (selectedGalleryImage !== null) {
      const src =
        imagePathsScreen[selectedGalleryCategory].paths[selectedGalleryImage]
      setImageSrc(src)
    }
  }, [selectedGalleryImage])

  useEffect(() => {
    if (galleryRef.current) {
      if (props.isVisible) {
        gsap.to(galleryRef.current, {
          autoAlpha: 1,
          // x: 0,
          duration: 0.6,
          ease: "power3.inOut",
        })
      } else {
        gsap.to(galleryRef.current, {
          autoAlpha: 0,
          // x: 0,
          duration: 0.6,
          ease: "power3.inOut",
        })
      }
    }
  }, [props.isVisible])

  useEffect(() => {
    if (transformWrapperRef?.current)
      transformWrapperRef?.current.setTransform(
        zoomRef?.[0],
        zoomRef?.[1],
        zoomRef?.[2],
      )
  }, [zoomRef])

  return (
    <GalleryWrapper ref={galleryRef} visible={props.isVisible}>
      <TransformWrapper
        ref={transformWrapperRef}
        smooth={true}
        minScale={1}
        maxScale={2.5}
      >
        <TransformComponent>
          <ImagesWrapper className="imagesWrapper">
            <Carousel
              slideEffect="fade"
              slides={imagePathsScreen[selectedGalleryCategory].paths}
            />
          </ImagesWrapper>
        </TransformComponent>
      </TransformWrapper>
    </GalleryWrapper>
  )
}

const GalleryWrapper = styled.div<{ visible: boolean }>`
  width: 100%;
  height: 100%;
  display: grid;
  background-color: white;
  position: absolute;
  z-index: 100;
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  opacity: ${(props) => (props.visible ? 1 : 0)};
  overflow: hidden;
  place-items: center;
`

const ImagesWrapper = styled.div`
  height: 100vh;
  width: 100vw;
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  display: grid;
  place-self: center;
  place-items: center;

  .image {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    transition: opacity 0.6s ease;
    overflow: hidden;
  }
`

export default Gallery
